import { LRUCache } from 'lru-cache'

import { DataType, cacheKeyForRecordType } from '../types/DataType'
import { LOCAL_STORAGE_DEVICE_ID_KEY, LOCAL_STORAGE_USER_ID_KEY } from '../utils/constants'
import { Device } from './Device'
import { PayloadType } from './SQLBuilder'
import { User } from './User'

type CurrentUserDetails = {
    id: string
    user?: User
    deviceId: string
    device?: Device
}

export class CacheFactory {
    private static instance: CacheFactory | null = null
    public static getInstance() {
        if (CacheFactory.instance == null) {
            CacheFactory.instance = new CacheFactory()
        }
        return CacheFactory.instance
    }

    // move current user and IDs out of LRU so they cannot be evicted
    private currentUserDetails: CurrentUserDetails
    private cache = new LRUCache({
        max: 5000,

        // return stale items before removing from cache?
        allowStale: false,

        updateAgeOnGet: true,
        updateAgeOnHas: false,
    })
    private updateBroadcastChannel = new BroadcastChannel('cache-update')

    private constructor() {
        this.currentUserDetails = {
            id: localStorage.getItem(LOCAL_STORAGE_USER_ID_KEY) ?? '',
            deviceId: localStorage.getItem(LOCAL_STORAGE_DEVICE_ID_KEY) ?? '',
        }

        this.updateBroadcastChannel.onmessage = (event) => {
            if (event.data === 'clearAll') {
                this.cache.clear()
                this.currentUserDetails = {
                    id: '',
                    deviceId: '',
                }
                return
            }

            const ourObj = this.cache.get(event.data.key) as
                | { last_modified_date?: string }
                | undefined

            // only update if we don't have the object, or if the remote object is newer
            if (
                !ourObj ||
                (ourObj.last_modified_date &&
                    ourObj.last_modified_date < event.data.obj.last_modified_date)
            ) {
                this.cache.set(event.data.key, event.data.obj)
            }
        }
    }

    get userId() {
        if (this.currentUserDetails?.id) return this.currentUserDetails.id

        this.currentUserDetails = {
            ...this.currentUserDetails,
            id: localStorage.getItem(LOCAL_STORAGE_USER_ID_KEY) ?? '',
        }

        return this.currentUserDetails.id
    }

    get deviceId() {
        if (this.currentUserDetails?.deviceId) return this.currentUserDetails.deviceId

        this.currentUserDetails = {
            ...this.currentUserDetails,
            deviceId: localStorage.getItem(LOCAL_STORAGE_DEVICE_ID_KEY) ?? '',
        }

        return this.currentUserDetails.deviceId
    }

    private compositeKey(type: DataType, key: string) {
        return `${type}_${key}`
    }

    removeRecord(type: DataType, ...objs: any[]) {
        this.update(
            ...objs.map((obj) => {
                return {
                    type,
                    data: { jsonData: JSON.stringify(obj) },
                } as PayloadType
            })
        )
    }

    recordForKey(key: string, model: any) {
        if ('dataType' in model) {
            const type = model.dataType() as DataType
            const ck = this.compositeKey(type, key)
            if (this.cache.has(ck)) {
                return new model(this.cache.get(ck))
            }
        }
        return undefined
    }

    update(...payload: PayloadType[]) {
        for (const row of payload) {
            if (!row.data || !('jsonData' in row.data)) continue

            const obj = JSON.parse(row.data['jsonData'])
            const ck = this.compositeKey(row.type, obj[cacheKeyForRecordType(row.type)])

            this.cache.set(ck, obj)

            if (row.type === 'users' && obj.id === this.currentUserDetails?.id) {
                this.currentUserDetails = {
                    ...this.currentUserDetails,
                    user: new User(obj),
                }
            }
            else if (row.type === 'devices' && obj.device === this.currentUserDetails?.deviceId) {
                this.currentUserDetails = {
                    ...this.currentUserDetails,
                    device: new Device(obj),
                }
            }

            this.updateBroadcastChannel.postMessage({
                key: ck,
                obj,
            })
        }
    }

    getRecord(model: any, id: string | undefined) {
        const ck = this.compositeKey(model.dataType(), id || '')
        const obj = this.cache.get(ck)
        return obj ? new model(obj) : undefined
    }

    currentUser(): User | undefined {
        if (this.currentUserDetails?.user) return this.currentUserDetails.user

        let user: User | undefined = undefined
        if (this.userId && this.cache.has(this.compositeKey('users', this.userId))) {
            user = new User(this.cache.get(this.compositeKey('users', this.userId)))
            this.currentUserDetails = {
                ...this.currentUserDetails,
                user,
            }
        }
        return user
    }

    currentDevice(): Device | undefined {
        if (this.currentUserDetails?.device) return this.currentUserDetails.device

        let device = undefined
        if (this.deviceId && this.cache.has(this.compositeKey('devices', this.deviceId))) {
            device = new Device(this.cache.get(this.compositeKey('devices', this.deviceId)))
            this.currentUserDetails = {
                ...this.currentUserDetails,
                device,
            }
        }
        return device
    }

    clearAll() {
        // used to empty cache on logout
        this.cache.clear()
        this.currentUserDetails = {
            id: '',
            deviceId: '',
        }
        this.updateBroadcastChannel.postMessage('clearAll')
    }

    addObject(keyString: string, object: any) {
        this.cache.set(keyString, object)
    }

    objectForKey<T = any>(keyString: string): T | undefined {
        return this.cache.get(keyString) as T | undefined
    }
}
